import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useRoomStatus, useRooms } from '../api/queries';
import type { Room } from '../api/types';
import { ROOM_STATUS } from '../format';
import { Badge, Button, Empty, ErrorNote, Loading, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

type Filter = 'all' | 'dirty' | 'ready' | 'occupied' | 'ooo';

const FILTERS: { id: Filter; label: string; test: (r: Room) => boolean }[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'dirty', label: 'To clean', test: (r) => r.status === 'VACANT_DIRTY' },
  { id: 'ready', label: 'Ready', test: (r) => r.status === 'VACANT_CLEAN' || r.status === 'INSPECTED' },
  { id: 'occupied', label: 'Occupied', test: (r) => r.status === 'OCCUPIED' },
  { id: 'ooo', label: 'Out of order', test: (r) => r.status === 'OUT_OF_ORDER' },
];

export function Rooms() {
  const { can } = useAuth();
  const rooms = useRooms();
  const setStatus = useRoomStatus();
  const toast = useToast();
  const report = useReport();
  const [filter, setFilter] = useState<Filter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const canClean = can('room.status.update');

  const list = (rooms.data ?? []).filter(FILTERS.find((f) => f.id === filter)!.test);
  const count = (f: Filter) => (rooms.data ?? []).filter(FILTERS.find((x) => x.id === f)!.test).length;

  function change(room: Room, status: 'VACANT_CLEAN' | 'VACANT_DIRTY' | 'INSPECTED') {
    setBusyId(room.id);
    setStatus.mutate(
      { roomId: room.id, status, number: room.number },
      {
        onSuccess: () => toast.ok(`Room ${room.number} marked ${ROOM_STATUS[status]?.label.toLowerCase()}`),
        onError: report,
        onSettled: () => setBusyId(null),
      },
    );
  }

  return (
    <div className="page">
      <PageHead eyebrow="Housekeeping" title="Rooms" />

      <div className="seg" role="group" aria-label="Filter rooms">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label} {rooms.data ? <span className="num muted">{count(f.id)}</span> : null}
          </button>
        ))}
      </div>

      {rooms.isLoading ? (
        <Loading rows={3} />
      ) : rooms.error ? (
        <ErrorNote error={rooms.error} retry={() => void rooms.refetch()} />
      ) : list.length === 0 ? (
        <div className="card flat">
          <Empty icon="rooms" title={(rooms.data ?? []).length === 0 ? 'No rooms set up yet' : 'No rooms match this filter'} />
        </div>
      ) : (
        <div className="rooms">
          {list.map((r) => {
            const st = ROOM_STATUS[r.status]!;
            const editable = canClean && r.status !== 'OCCUPIED' && r.status !== 'OUT_OF_ORDER';
            return (
              <div key={r.id} className={`room s-${r.status}`}>
                <Link to={`/rooms/${r.id}`} className="room-open" aria-label={`Room ${r.number}, ${r.roomType.name}: see details`}>
                  <span className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="no num">{r.number}</span>
                    <Badge tone={st.tone}>{st.label}</Badge>
                  </span>
                  <span className="small muted">{r.roomType.name} · details</span>
                </Link>
                {editable ? (
                  <div className="row">
                    {r.status === 'VACANT_DIRTY' ? (
                      <Button variant="primary" loading={busyId === r.id} onClick={() => change(r, 'VACANT_CLEAN')}>
                        Mark clean
                      </Button>
                    ) : (
                      <Button loading={busyId === r.id} onClick={() => change(r, 'VACANT_DIRTY')}>
                        Mark dirty
                      </Button>
                    )}
                    {r.status === 'VACANT_CLEAN' ? (
                      <Button variant="ghost" loading={busyId === r.id} onClick={() => change(r, 'INSPECTED')}>
                        Inspected
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
